// packages/frontend/src/services/tokenExpirationMonitor.ts

import { auth } from './firebase';
import type { User as FirebaseUser } from 'firebase/auth';
import { createLogger } from '../utils/logger';
import { useEnhancedAuthStore } from '../stores/enhancedAuth';
import { AUTH_CONFIG } from '../config/authConfig';
import { showLogoutDialog, hideLogoutDialog } from '../components/auth/LogoutDialog';

const logger = createLogger('TokenExpirationMonitor');

export interface TokenExpirationMonitorOptions {
  onExpiration: () => Promise<void>;
  onExpiringSoon?: (minutesRemaining: number) => void;
}

export interface TokenExpirationMonitor {
  start: () => void;
  stop: () => void;
}

/**
 * Check if Firebase token is expired or expiring soon
 * Exported for use in components that need to check token expiration proactively
 */
export async function checkFirebaseTokenExpiration(
  firebaseUser: FirebaseUser | null
): Promise<{ expired: boolean; expiringSoon: boolean; minutesRemaining: number }> {
  if (!firebaseUser) {
    return { expired: true, expiringSoon: false, minutesRemaining: 0 };
  }

  try {
    const tokenResult = await firebaseUser.getIdTokenResult();
    const expirationTime = tokenResult.expirationTime
      ? new Date(tokenResult.expirationTime).getTime()
      : null;

    if (!expirationTime) {
      logger.warn('Token expiration time not available');
      return { expired: false, expiringSoon: false, minutesRemaining: Infinity };
    }

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;
    const minutesRemaining = Math.floor(timeUntilExpiration / (60 * 1000));
    const warningThreshold = AUTH_CONFIG.TOKEN_EXPIRATION_WARNING_MINUTES * 60 * 1000;

    const expired = timeUntilExpiration <= 0;
    const expiringSoon = !expired && timeUntilExpiration <= warningThreshold;

    return { expired, expiringSoon, minutesRemaining };
  } catch (error) {
    logger.error('Failed to check Firebase token expiration', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return { expired: true, expiringSoon: false, minutesRemaining: 0 };
  }
}

/**
 * Check if JWT token is expired or expiring soon
 */
function checkJWTTokenExpiration(): {
  expired: boolean;
  expiringSoon: boolean;
  minutesRemaining: number;
} {
  const { session } = useEnhancedAuthStore.getState();

  if (!session?.expiresAt) {
    return { expired: false, expiringSoon: false, minutesRemaining: Infinity };
  }

  const now = Date.now();
  const expirationTime = new Date(session.expiresAt).getTime();
  const timeUntilExpiration = expirationTime - now;
  const minutesRemaining = Math.floor(timeUntilExpiration / (60 * 1000));
  const warningThreshold = AUTH_CONFIG.TOKEN_EXPIRATION_WARNING_MINUTES * 60 * 1000;

  const expired = timeUntilExpiration <= 0;
  const expiringSoon = !expired && timeUntilExpiration <= warningThreshold;

  return { expired, expiringSoon, minutesRemaining };
}

/**
 * Attempt to refresh Firebase token
 */
async function refreshFirebaseToken(
  firebaseUser: FirebaseUser | null
): Promise<boolean> {
  if (!firebaseUser) {
    return false;
  }

  try {
    // Force token refresh
    await firebaseUser.getIdToken(true);
    logger.info('Firebase token refreshed successfully');
    return true;
  } catch (error) {
    logger.error('Failed to refresh Firebase token', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return false;
  }
}

/**
 * Start token expiration monitoring
 */
export function startTokenExpirationMonitor(
  options: TokenExpirationMonitorOptions
): () => void {
  let intervalId: NodeJS.Timeout | null = null;
  let warningShown = false;

  // Every 60s tick starts its own independent checkExpiration; nothing
  // prevents them from overlapping. Rather than trusting the token snapshot
  // taken before a dialog parked, re-derive validity from current state once
  // the dialog resolves: a later, independent check may have refreshed the
  // token successfully while this one was waiting for acknowledgement
  // (formal finding 7, NoLogoutWithValidToken).
  const isStillExpired = async (): Promise<boolean> => {
    const currentFirebaseUser = auth.currentUser;
    const globalState = useEnhancedAuthStore.getState();
    const isUsingFirebase = !!currentFirebaseUser && globalState.isAuthenticated;
    if (isUsingFirebase) {
      return (await checkFirebaseTokenExpiration(currentFirebaseUser)).expired;
    }
    return checkJWTTokenExpiration().expired;
  };

  // Shows the blocking logout dialog, then re-verifies before logging out
  // instead of trusting the resolved dialog unconditionally.
  const parkOnDialogThenMaybeLogout = async (): Promise<void> => {
    try {
      await showLogoutDialog();
      logger.info('Logout dialog resolved, re-checking token before logout');
    } catch (dialogError) {
      logger.warn('Failed to show logout dialog', {
        error: dialogError instanceof Error ? dialogError.message : 'Unknown error',
      });
      // If dialog fails, proceed with logout anyway
    }

    if (await isStillExpired()) {
      await options.onExpiration();
    } else {
      logger.info('Token was refreshed while the logout dialog was parked; skipping logout');
    }
  };

  const checkExpiration = async () => {
    try {
      const firebaseUser = auth.currentUser;
      const globalState = useEnhancedAuthStore.getState();

      // Check Firebase token expiration
      const firebaseCheck = await checkFirebaseTokenExpiration(firebaseUser);

      // Check JWT token expiration (if using JWT auth)
      const jwtCheck = checkJWTTokenExpiration();

      // Determine which token to use (Firebase takes precedence)
      const isUsingFirebase = !!firebaseUser && globalState.isAuthenticated;
      const tokenCheck = isUsingFirebase ? firebaseCheck : jwtCheck;

      // Handle expired token
      if (tokenCheck.expired) {
        logger.warn('Token expired, attempting refresh before logout');

        // Try to refresh Firebase token if available
        if (firebaseUser) {
          const refreshed = await refreshFirebaseToken(firebaseUser);
          if (refreshed) {
            // A different, earlier check's dialog may still be parked open;
            // this successful refresh makes it stale, so retract it.
            hideLogoutDialog();
          } else {
            logger.error('Token refresh failed, showing logout dialog');
            await parkOnDialogThenMaybeLogout();
            return;
          }
        } else {
          // JWT token expired and no Firebase user - logout
          logger.error('JWT token expired and no Firebase user, showing logout dialog');
          await parkOnDialogThenMaybeLogout();
          return;
        }
      }

      // Handle expiring soon
      if (tokenCheck.expiringSoon && !warningShown && options.onExpiringSoon) {
        warningShown = true;
        options.onExpiringSoon(tokenCheck.minutesRemaining);
        logger.info('Token expiring soon', {
          minutesRemaining: tokenCheck.minutesRemaining,
        });
      }

      // Reset warning flag if token is no longer expiring soon
      if (!tokenCheck.expiringSoon) {
        warningShown = false;
      }

      // Proactively refresh token if expiring soon
      if (tokenCheck.expiringSoon && firebaseUser) {
        await refreshFirebaseToken(firebaseUser);
      }
    } catch (error) {
      logger.error('Error in token expiration check', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  // Start monitoring
  intervalId = setInterval(checkExpiration, AUTH_CONFIG.TOKEN_CHECK_INTERVAL_MS);
  
  // Run initial check immediately
  checkExpiration();

  logger.info('Token expiration monitor started', {
    checkInterval: AUTH_CONFIG.TOKEN_CHECK_INTERVAL_MS,
    warningMinutes: AUTH_CONFIG.TOKEN_EXPIRATION_WARNING_MINUTES,
  });

  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
      logger.info('Token expiration monitor stopped');
    }
  };
}

